import { Component, computed, Inject, input, InputSignal, Signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { TT_COM_PROCESSING_ENDPOINT } from '@candyland/angular/shared/util-nmo';

import {
  buildImageUrl,
  FileFormat,
  FullImageSize,
  MimeType,
  ProcessingConfig,
  ResizeOptions,
} from '@candyland/generic/shared/tt-com-processing/util';

/**
 * documentation on this component:
 * https://mohomedia.atlassian.net/wiki/spaces/EN/pages/3510370305/image-source-set+component
 */
@Component({
  selector: 'lib-image-source-set',
  imports: [NgOptimizedImage],
  templateUrl: './image-source-set.component.html',
  styleUrls: ['./image-source-set.component.scss'],
})
export class ImageSourceSetComponent {
  sourceConfig: InputSignal<Source | Source[]> = input();
  defaultCropConfig: InputSignal<ProcessingConfig> = input();
  alt: InputSignal<string> = input();
  priority: InputSignal<boolean> = input(false);
  title: InputSignal<string | null> = input<string | null>(null);

  sourcesConfig: Signal<Source[]> = computed((): Source[] => {
    const source: Source | Source[] = this.sourceConfig();
    return structuredClone<Source[]>(Array.isArray(source) ? source : [source]);
  });

  fallbackSource: Signal<string> = computed((): string => {
    const crop: ProcessingConfig = structuredClone<ProcessingConfig>(this.defaultCropConfig());
    if (!crop.format) {
      crop.format = 'avif';
    }
    const uri = this.sourcesConfig()[0].uri;
    return buildImageUrl(`${this.getProcessingEndpointUrl()}/image/${uri}`, crop);
  });

  sourceSet: Signal<Array<SourceInterface>> = computed((): Array<SourceInterface> => {
    const sources: Array<SourceInterface> = [];
    for (const source of this.sourcesConfig()) {
      //const imageType: ImageType = ImageSourceSetComponent.getImageType(source.)
      // types that should be generated.
      const sourceSetMap: SourceSetMap = ImageSourceSetComponent.getSourceSetMap(source);
      const imageUrl = `${this.getProcessingEndpointUrl()}/image/${source.uri}`;
      for (const version of source.versions) {
        for (const imageType of Object.keys(sourceSetMap)) {
          const set: { sourceSet: string[]; sourceSetSizes: string[]; sizes: string[] } = sourceSetMap[imageType];
          version.format = ImageSourceSetComponent.getImageFormat(imageType as MimeType) || 'jpeg';
          const url: string = buildImageUrl(imageUrl, version);
          set.sourceSet.push(`${url} ${version.resize.width}w`);
          set.sizes.push(source.containerWidth ? `${source.containerWidth}px` : '100vw');
        }
      }
      for (const imageType of Object.keys(sourceSetMap)) {
        sources.push({
          type: imageType,
          sourceSet: sourceSetMap[imageType].sourceSet,
          sourceSetSize: sourceSetMap[imageType].sourceSetSizes,
          sizes: sourceSetMap[imageType].sizes,
          media: source.media,
        });
      }
    }
    return sources;
  });

  constructor(@Inject(TT_COM_PROCESSING_ENDPOINT) private readonly ttComProcessingEndpoint: string) {}

  public static get defaultMobileMedia(): string {
    return '(max-width: 1023px)';
  }
  public static get defaultDesktopMedia(): string {
    return '(min-width: 1024px)';
  }

  private static getSourceSetMap(source: Source): SourceSetMap {
    const res: SourceSetMap = {};
    for (const version of source.versions) {
      const type: MimeType = ImageSourceSetComponent.getImageType(version.format);
      if (type !== null && !res[type]) {
        res[type] = { sourceSet: [], sourceSetSizes: [], sizes: [] };
      }
    }
    return Object.keys(res).length === 0
      ? {
          'image/avif': { sourceSet: [], sourceSetSizes: [], sizes: [] },
          'image/jpeg': { sourceSet: [], sourceSetSizes: [], sizes: [] },
        }
      : res;
  }

  private static getImageType(imageFormat: FileFormat): MimeType | null {
    switch (imageFormat) {
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'gif':
        return 'image/gif';
      case 'jp2':
        return 'image/jp2';
      case 'tiff':
        return 'image/tiff';
      case 'avif':
        return 'image/avif';
      case 'heif':
        return 'image/heif';
      default:
        return null;
    }
  }

  private static getImageFormat(imageType: MimeType): FileFormat | null {
    switch (imageType) {
      case 'image/jpeg':
        return 'jpeg';
      case 'image/png':
        return 'png';
      case 'image/webp':
        return 'webp';
      case 'image/gif':
        return 'gif';
      case 'image/jp2':
        return 'jp2';
      case 'image/tiff':
        return 'tiff';
      case 'image/avif':
        return 'avif';
      case 'image/heif':
        return 'heif';
      default:
        return null;
    }
  }

  private getProcessingEndpointUrl(): string {
    const endpoint = this.ttComProcessingEndpoint;
    return endpoint.startsWith('http://') || endpoint.startsWith('https://') ? endpoint : `https://${endpoint}`;
  }
}

interface SourceSetMap {
  [type: string]: {
    sourceSet: string[];
    sourceSetSizes: string[];
    sizes: string[];
  };
}

interface SourceInterface {
  type: string;
  sourceSet: string[];
  sourceSetSize: string[];
  sizes: string[];
  media?: string;
}

export interface Source {
  uri: string;
  versions: Array<ProcessingConfig>;
  containerWidth?: number;
  media?: string;
}

export function getResizeByProportion(source: ProcessingConfig, proportion: number): ProcessingConfig {
  if (!source.resize) {
    return source;
  }

  const resize: ResizeOptions = { ...source.resize };

  if (resize.width) {
    resize.width = Math.ceil(source.resize.width * proportion);
  }
  if (resize.height) {
    resize.height = Math.ceil(source.resize.height * proportion);
  }

  return { ...source, resize };
}

/**
 * returns width and height for an image
 * if only width and aspect-ratio is provided it returns the calculated height
 * if maxHeight is provided it will calculate the width if the height would be greater than maxHeigh (fit inside)
 * @param {number} width
 * @param {number} aspectRatio
 * @param {number|null} [maxHeight=null]
 * @return {FullImageSize}
 */
export function getDimensionByAspectRatio(
  width: number,
  aspectRatio: number,
  maxHeight: number | null = null
): FullImageSize {
  const res: FullImageSize = {
    width,
    height: Math.round(width / aspectRatio),
  };

  if (maxHeight !== null && res.height > maxHeight) {
    res.width = Math.round(maxHeight * aspectRatio);
    res.height = maxHeight;
  }

  return res;
}
